import { WithChildren } from '@/types'
import { SignalRConnection } from '@/types/signalr'
import { createContext } from '@/ultils'
import { HubConnection, HubConnectionState } from '@microsoft/signalr'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RealtimeHubService from '.'

interface State {}

interface SignalRContextValues extends State {
  getHubConnect: () => Promise<HubConnection>
  // Ref-counted group membership. SignalR groups are per-CONNECTION, and this
  // app shares ONE connection across components (grid, sidebar, etc.). If each
  // component invoked JoinGroup/LeaveGroup directly, one component leaving would
  // remove the shared connection from the group and silence every other
  // component still listening. Route joins/leaves through these so LeaveGroup
  // only fires when the last subscriber for a group unmounts.
  joinGroup: (groupName: string) => Promise<void>
  leaveGroup: (groupName: string) => Promise<void>
}

const [Provider, useSignalR] = createContext<SignalRContextValues>({
  name: 'SignalR',
})

const SignalRProvider = ({ children }: WithChildren) => {
  const [connection, setConnection] = useState<HubConnection>()
  // groupName -> number of active subscribers
  const groupCounts = useRef<Map<string, number>>(new Map())

  const getHubConnect = useCallback(async () => {
    let _connection = connection
    if (!_connection) {
      _connection = new RealtimeHubService().getHubConnect()
    }

    if (_connection.state === HubConnectionState.Disconnected) {
      await _connection.start()
    }

    setConnection(_connection)
    return _connection
  }, [connection])

  const joinGroup = useCallback(
    async (groupName: string) => {
      const conn = await getHubConnect()
      const next = (groupCounts.current.get(groupName) || 0) + 1
      groupCounts.current.set(groupName, next)
      // Only the first subscriber actually joins the group on the server.
      if (next === 1) {
        await conn.invoke(SignalRConnection.JOIN_GROUP, groupName)
      }
    },
    [getHubConnect],
  )

  const leaveGroup = useCallback(
    async (groupName: string) => {
      const next = (groupCounts.current.get(groupName) || 0) - 1
      if (next > 0) {
        groupCounts.current.set(groupName, next)
        return
      }
      // Last subscriber left — drop the count and leave the group on the server.
      groupCounts.current.delete(groupName)
      if (connection?.state === HubConnectionState.Connected) {
        try {
          await connection.invoke(SignalRConnection.LEAVE_GROUP, groupName)
        } catch {
          // ignore — connection may be tearing down
        }
      }
    },
    [connection],
  )

  // Group memberships are bound to the connection id, so an automatic reconnect
  // (new connection id) silently drops every group. Rejoin the still-active
  // groups whenever the connection comes back.
  useEffect(() => {
    if (!connection) return
    const rejoinActiveGroups = () => {
      groupCounts.current.forEach((count, groupName) => {
        if (count > 0) {
          connection
            .invoke(SignalRConnection.JOIN_GROUP, groupName)
            .catch(() => {})
        }
      })
    }
    connection.onreconnected(rejoinActiveGroups)
  }, [connection])

  useEffect(() => {
    const connection = new RealtimeHubService().getHubConnect()
    setConnection(connection)
    return () => {
      connection.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(() => {
    return {
      getHubConnect,
      joinGroup,
      leaveGroup,
    }
  }, [getHubConnect, joinGroup, leaveGroup])

  return <Provider value={value}>{children}</Provider>
}

export { SignalRProvider, useSignalR }
